import { getReportsData,getGraphReportPDF } from "../../../api/report";
import React, { useState, useEffect } from "react";
import i18next from "i18next";
import Stack from "@mui/material/Stack";
import exportIcon from "../../../images/export.svg";
import GeneralButton from "../../common/SaveButton/GeneralButton";
import { showError, showSuccess } from "../../../utils/showMessage";
import { ExportModal } from "../../common/CustomModal/ExportModal";
import CustomReport from "../../common/CustomModal/CustomReport";
import Popup from "reactjs-popup";
import { changeSelectedValuesList } from "../../../state/slices/advance_filter";
import { VscPreview } from "react-icons/vsc";
import { FaRegEdit } from "react-icons/fa";
import { LazyLoadImage } from "react-lazy-load-image-component";
import { toast } from "react-toastify";
import { useAppTheme } from "../../../theme/useTheme";
import { useTranslation } from "react-i18next";
import "./report.scss";


export const formatReportType = (type) => {
  const formattedReportType = getProperReportName(type);
  
  return formattedReportType
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ")
    .replace("Audit", "")
    .trim();
};

export const getProperReportName = (reportType) => {
  let result = reportType;
  
  if (reportType.toLowerCase().includes('asset')) {
    result = result.replace(/asset/gi, 'server');
  }
  
  if (reportType.toLowerCase().includes('app') && !reportType.toLowerCase().includes('web_app')) {
    result = result.replace(/app/gi, 'database');
  }
  
  return result;
};

export const ActionComponent = ({
  getData,
  search,
  pageType,
  filter_by,
  advanceFilters,
  disable_buttons,
  Columns,
  id,
}) => {
  const { theme } = useAppTheme();
  const { t } = useTranslation();
  const [modalOpen, setModalOpen] = useState(false);
  const handleOpen = () => setModalOpen(true);
  const handleClose = () => setModalOpen(false);

  const [customOpen, setCustomOpen] = useState(false);
  const handleCustomOpen = () => setCustomOpen(true);
  const handleCustomClose = () => setCustomOpen(false);

  return (
    <>
      <Stack direction="row" spacing={2} className="py-3">
        {disable_buttons && (
          <GeneralButton
            onClickEvent={handleOpen}
            color="#000000"
            variant="text"
            size="large"
            value={t("reports.exportReports")}
            startIcon={
              <LazyLoadImage
                src={exportIcon}
                alt="icon"
                className="reports-export-icon"
              />
            }
          />
        )}
        {disable_buttons && (
          <GeneralButton
            onClickEvent={handleCustomOpen}
            color="#000000"
            variant="text"
            size="large"
            value={t("reports.customReports")}
            startIcon={
              <FaRegEdit
                alt="icon"
                size={20}
                className="reports-custom-icon"
                style={{ color: theme.palette.text.secondary }}
              />
            }
          />
        )}
      </Stack>

      <ExportModal
        open={modalOpen}
        handleClose={handleClose}
        getData={getData}
        search={search}
        pageType={pageType}
        filterBy={filter_by}
        advanceFilters={advanceFilters}
        id={id}
      />
      <CustomReport
        open={customOpen}
        handleClose={handleCustomClose}
        getData={getData}
        search={search}
        pageType={pageType}
        filterBy={filter_by}
        advanceFilters={advanceFilters}
        columns={Columns}
        id={id}
      />
    </>
  );
};



export const fetchData = async (
  page,
  perPage,
  search,
  report_type,
  response_type,
  filterBy,
  setReportData,
  setTotalReportData,
  setFilteredData,
  setLoading,
  setPending,
  setAllFields,
  allFields,
  keysToExclude,
  advanceFilters,
  dispatch,
  selectedValuesList,
  selectedColumnValues
) => {
  setLoading(true);

  const requestData = {
    page,
    page_size: perPage,
    search,
    report_type,
    response_type,
    filter_by: filterBy,
    advance_filters: advanceFilters,
  };

  try {
    if (response_type === "pdf") {
      await toast.promise(getGraphReportPDF(requestData).then((result) => {
        const { data } = result;
        const url = window.URL.createObjectURL(new Blob([data.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `${getProperReportName(report_type)}.pdf`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }), {
        pending: i18next.t("reports.generatingReport"),
        success: i18next.t("reports.generatedPdfSuccess", { reportType: formatReportType(report_type) }),
        error: i18next.t("reports.failedGenerateReport"),
      }, {
        closeButton: true
      });
    } else if (response_type === "csv") {
      // Add waiting toast for CSV export
      await toast.promise(getReportsData({
        page: page,
        page_size: perPage,
        search: search,
        report_type: report_type,
        response_type: response_type,
        filter_by: filterBy,
        advance_filters: advanceFilters,
        columns: selectedColumnValues,
      }).then((result) => {
        const { data, error } = result;
        if (data !== null) {
          const url = window.URL.createObjectURL(new Blob([data.data]));
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", getProperReportName(report_type) + ".csv");
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        } else {
          throw new Error(error);
        }
      }), {
        pending: i18next.t("reports.generatingCsvReport"),
        success: i18next.t("reports.generatedCsvSuccess", { reportType: formatReportType(report_type) }),
        error: i18next.t("reports.failedGenerateCsvReport"),
      }, {
        closeButton: true
      });
    } else {
      const { data, error } = await getReportsData({
        page: page,
        page_size: perPage,
        search: search,
        report_type: report_type,
        response_type: response_type,
        filter_by: filterBy,
        advance_filters: advanceFilters,
        columns: selectedColumnValues,
      });

      if (data !== null && response_type === "table") {
        setReportData(data.results || []);
        let tempAllFields =
          data.count > 0
            ? Object.keys(data.results[0]).filter(
                (key) => !keysToExclude.includes(key)
              )
            : [];
        tempAllFields = [...tempAllFields, ...allFields];
        setAllFields([...new Set(tempAllFields)]);
        if (
          selectedValuesList[report_type] &&
          selectedValuesList[report_type].length === 0
        ) {
          let tempSelectedValuesList = { ...selectedValuesList };
          tempSelectedValuesList = {
            ...tempSelectedValuesList,
            [report_type]: [...new Set(tempAllFields)],
          };
          dispatch(changeSelectedValuesList(tempSelectedValuesList));
        }

        setTotalReportData(data.count);
        setFilteredData(data.results);
      } else {
        showError(error);
      }
    }
  } catch (error) {
    showError(error?.message || "Unknown error occurred");
  }

  setLoading(false);
  setPending(false);
};






